import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { authedJson } from '@/lib/http';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface SearchResult {
  type: string;
  id: string;
  label: string;
  sublabel?: string;
  href?: string;
  section?: string;
}

function GlobalSearch({ onNavigate }: { onNavigate: (section: string) => void }) {
  const [q, setQ] = useState('');
  const { data } = useQuery<unknown>({
    queryKey: ['/api/admin/search', q],
    queryFn: () => authedJson(`/api/admin/search?q=${encodeURIComponent(q.trim())}`),
    enabled: q.trim().length >= 2,
  });
  const results = asArray<SearchResult>(data);

  const go = (r: SearchResult) => {
    setQ('');
    if (r.href) window.location.href = r.href;
    else if (r.section) onNavigate(r.section);
  };

  return (
    <div className="relative">
      <Input
        placeholder="Search creators, classes, users…"
        value={q}
        onChange={e => setQ(e.target.value)}
        aria-label="Global admin search"
      />
      {q.trim().length >= 2 && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border bg-background shadow-lg">
          {results.slice(0, 12).map(r => (
            <button
              key={`${r.type}-${r.id}`}
              type="button"
              onClick={() => go(r)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
            >
              <Badge variant="outline" className="text-xs capitalize">
                {r.type}
              </Badge>
              <span className="min-w-0 flex-1 truncate">{r.label}</span>
              {r.sublabel && (
                <span className="truncate text-xs text-muted-foreground">{r.sublabel}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const asArray = <T,>(v: unknown): T[] =>
  Array.isArray(v) ? (v as T[]) : (((v as { data?: T[] } | null)?.data ?? []) as T[]);
const n = (v: unknown) => Number(v ?? 0);
const usd = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

interface OverviewResp {
  totals?: { netCollectedCents?: string | null; platformFeeCents?: string | null };
}

/** A queue definition: label, the endpoint to count, and the admin section to open. */
interface Queue {
  key: string;
  label: string;
  url: string;
  section: string;
  /**
   * Optional predicate to count only items that still need attention. Used when
   * an endpoint returns all rows (not just open ones) and can't be filtered via
   * a query param.
   */
  filter?: (item: unknown) => boolean;
}

/** A refund request is "open" (unaddressed) only while it can still be acted on. */
const isOpenRefundRequest = (item: unknown): boolean => {
  const status = (item as { status?: string })?.status;
  return status === 'pending' || status === 'creator_review';
};

const QUEUES: Queue[] = [
  {
    key: 'creator-apps',
    label: 'Creator applications',
    url: '/api/admin/creator-applications?status=pending',
    section: 'creator-apps',
  },
  {
    key: 'refunds',
    label: 'Refund requests',
    url: '/api/refunds/requests/admin',
    section: 'refunds',
    filter: isOpenRefundRequest,
  },
  {
    key: 'moderation',
    label: 'Moderation reports',
    url: '/api/admin/community/reports?status=pending',
    section: 'moderation',
  },
  {
    key: 'payouts-blocked',
    label: 'Blocked payouts',
    url: '/api/admin/financials/payouts?status=blocked',
    section: 'payments',
  },
  {
    key: 'submissions',
    label: 'Calendar submissions',
    url: '/api/admin/calendar/submissions?status=pending',
    section: 'calendar',
  },
  {
    key: 'dead-letters',
    label: 'Failed emails',
    url: '/api/admin/email/dead-letters',
    section: 'email-ops',
  },
];

function QueueCard({ q, onNavigate }: { q: Queue; onNavigate: (s: string) => void }) {
  const { data, isLoading } = useQuery<unknown>({
    queryKey: [q.url],
    queryFn: () => authedJson(q.url),
  });
  const items = asArray(data);
  const count = (q.filter ? items.filter(q.filter) : items).length;
  const active = count > 0;
  return (
    <button
      type="button"
      onClick={() => onNavigate(q.section)}
      className={`rounded-xl border p-4 text-left transition-colors hover:border-primary/50 ${
        active ? 'border-orange-300 bg-orange-50/50' : 'border-border'
      }`}
    >
      <p className="text-2xl font-bold tabular-nums text-foreground">{isLoading ? '·' : count}</p>
      <p className="mt-0.5 text-sm text-muted-foreground">{q.label}</p>
    </button>
  );
}

export function AdminHome({ onNavigate }: { onNavigate: (section: string) => void }) {
  const { data: overview } = useQuery<OverviewResp>({
    queryKey: ['/api/admin/financials/overview', 'home'],
    queryFn: () => authedJson<OverviewResp>('/api/admin/financials/overview'),
  });
  const sales = n(overview?.totals?.netCollectedCents);
  const platform = n(overview?.totals?.platformFeeCents);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Overview</h2>
        <p className="text-sm text-muted-foreground">
          What needs your attention, and how the platform's doing.
        </p>
      </div>

      {/* Global search */}
      <GlobalSearch onNavigate={onNavigate} />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total sales</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{usd(sales)}</p>
          </CardContent>
        </Card>
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Platform income</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{usd(platform)}</p>
          </CardContent>
        </Card>
        <Card className="col-span-2 sm:col-span-2">
          <CardContent className="flex h-full items-center p-4">
            <button
              type="button"
              onClick={() => onNavigate('payments')}
              className="text-sm font-medium text-primary hover:underline"
            >
              Open full financial dashboard →
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Action queues */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Needs attention</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {QUEUES.map(q => (
              <QueueCard key={q.key} q={q} onNavigate={onNavigate} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
